import { usePostHog } from "@posthog/react";
import * as Sentry from "@sentry/tanstackstart-react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { AlertTriangleIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/base/button";
import { title } from "@/components/base/primitives";
import { DefaultLayout } from "@/layouts/default";

export function DefaultErrorContent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    Sentry.captureException(error);
    posthog.captureException(error);
  }, [error, posthog]);

  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-xl flex-col space-y-8 py-16 text-center">
        <div className="flex justify-center">
          <AlertTriangleIcon className="h-16 w-16 text-gray-400" />
        </div>
        <h1 className={title({ size: "lg" })}>Something Went Wrong</h1>
        <p className="mx-auto max-w-sm text-center text-gray-600">
          {(error instanceof Error && error.message) ||
            "An unexpected error occurred."}
        </p>
        <div className="flex flex-col justify-center gap-3 pt-4 sm:flex-row">
          <Button
            color="primary"
            isDisabled={isRetrying}
            onPress={() => {
              setIsRetrying(true);
              reset();
              router.invalidate();
            }}
          >
            {isRetrying ? "Retrying…" : "Try Again"}
          </Button>
          <Button
            variant="outline"
            color="secondary"
            onPress={() => {
              window.location.href = "/";
            }}
          >
            Go Home
          </Button>
        </div>
      </div>
    </DefaultLayout>
  );
}
